import { useState, useEffect, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useNavigate } from "react-router-dom";
import axios from "axios";
import { motion, useInView, AnimatePresence } from "framer-motion";
import { 
  Play, 
  Heart, 
  Users, 
  Church, 
  MapPin, 
  Calendar, 
  Clock, 
  ArrowRight, 
  Menu, 
  X,
  Youtube,
  Instagram,
  Facebook,
  Flame,
  Cross,
  Sparkles,
  HandHeart,
  Mic,
  BookOpen,
  Send,
  ExternalLink,
  Mail,
  Phone,
  Lock,
  LogOut,
  Eye,
  ChevronDown
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0 }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.15 }
  }
};

// Counter Component
const Counter = ({ end, suffix = "", duration = 2 }) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });

  useEffect(() => {
    if (isInView) {
      let start = 0;
      const increment = end / (duration * 60);
      const timer = setInterval(() => {
        start += increment;
        if (start >= end) {
          setCount(end);
          clearInterval(timer);
        } else {
          setCount(Math.floor(start));
        }
      }, 1000 / 60);
      return () => clearInterval(timer);
    }
  }, [isInView, end, duration]);

  return (
    <span ref={ref} className="stat-number">
      {count.toLocaleString()}{suffix}
    </span>
  );
};

// Navigation Component
const Navigation = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { name: "Home", href: "#home" },
    { name: "About", href: "#about" },
    { name: "Events", href: "#events" },
    { name: "Watch", href: "#watch" },
    { name: "Invite", href: "#invite" },
    { name: "Prayer", href: "#prayer" },
    { name: "Give", href: "#give" }
  ];

  return (
    <>
      <nav className={`floating-nav ${scrolled ? 'scrolled' : ''}`} data-testid="main-navigation">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <a href="#home" className="font-heading text-xl md:text-2xl tracking-wider text-white" data-testid="nav-logo">
            AMERICA <span className="gold-text">SAVED</span>
          </a>
          
          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <a 
                key={link.name} 
                href={link.href} 
                className="nav-link font-heading text-sm tracking-widest uppercase"
                data-testid={`nav-link-${link.name.toLowerCase()}`}
              >
                {link.name}
              </a>
            ))}
            <a 
              href="https://donorbox.org/indiasavedmission" 
              target="_blank" 
              rel="noopener noreferrer"
              className="btn-primary py-2 px-4 text-xs"
              data-testid="nav-give-btn"
            >
              Give Now
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button 
            className="md:hidden text-white p-2" 
            onClick={() => setMobileMenuOpen(true)}
            data-testid="mobile-menu-button"
          >
            <Menu size={24} />
          </button>
        </div>
      </nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div 
            className="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            data-testid="mobile-menu"
          >
            <button 
              className="absolute top-6 right-6 text-white p-2"
              onClick={() => setMobileMenuOpen(false)}
              data-testid="mobile-menu-close"
            >
              <X size={24} />
            </button>
            {navLinks.map((link) => (
              <a 
                key={link.name} 
                href={link.href} 
                className="font-heading text-2xl tracking-widest uppercase text-white hover:text-[#D4AF37] transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                {link.name}
              </a>
            ))}
            <a 
              href="https://donorbox.org/indiasavedmission" 
              target="_blank" 
              rel="noopener noreferrer"
              className="btn-primary mt-4"
              onClick={() => setMobileMenuOpen(false)}
            >
              Give Now
            </a>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

// Hero Section
const HeroSection = () => {
  return (
    <section 
      id="home" 
      className="hero-bg relative min-h-screen flex items-center justify-center"
      style={{ 
        backgroundImage: `url('https://customer-assets.emergentagent.com/job_saved-redesign/artifacts/29ihetqs_EV1.jpg')`
      }}
      data-testid="hero-section"
    >
      <div className="relative z-10 text-center px-6 max-w-5xl mx-auto">
        <motion.div
          initial="hidden"
          animate="visible"
          variants={staggerContainer}
        >
          <motion.p 
            variants={fadeInUp}
            className="font-accent text-[#D4AF37] text-sm md:text-base tracking-[0.3em] uppercase mb-6"
          >
            Revival in America
          </motion.p>
          
          <motion.h1 
            variants={fadeInUp}
            className="font-heading text-5xl md:text-7xl lg:text-8xl font-bold tracking-tighter uppercase text-white mb-6"
          >
            America <span className="gold-text">Saved</span> Mission
          </motion.h1>
          
          <motion.p 
            variants={fadeInUp}
            className="text-lg md:text-xl text-gray-300 mb-10 max-w-2xl mx-auto"
          >
            Experience the Power of Jesus Christ — Salvations, Healings, Deliverance, and Fresh Fire of the Holy Spirit
          </motion.p>
          
          <motion.div 
            variants={fadeInUp}
            className="flex flex-col sm:flex-row gap-4 justify-center"
          >
            <a 
              href="#events" 
              className="btn-primary inline-flex items-center justify-center gap-2"
              data-testid="hero-cta-primary"
            >
              Upcoming Events <ArrowRight size={18} />
            </a>
            <a 
              href="#watch" 
              className="btn-secondary inline-flex items-center justify-center gap-2"
              data-testid="hero-cta-secondary"
            >
              <Play size={18} /> Watch Now
            </a>
          </motion.div>
        </motion.div>
      </div>

      {/* Scroll indicator */}
      <motion.div 
        className="absolute bottom-10 left-1/2 -translate-x-1/2 z-10"
        animate={{ y: [0, 10, 0] }}
        transition={{ repeat: Infinity, duration: 2 }}
      >
        <div className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center pt-2">
          <div className="w-1 h-2 bg-[#D4AF37] rounded-full" />
        </div>
      </motion.div>
    </section>
  );
};

// Stats Section
const StatsSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const stats = [
    { number: 200000, suffix: "+", label: "Souls Reached", icon: Heart },
    { number: 154, suffix: "+", label: "Churches", icon: Church },
    { number: 18, suffix: "+", label: "States", icon: MapPin }
  ];

  return (
    <section className="py-20 md:py-32 bg-[#0A0A0B] relative" data-testid="stats-section">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div 
          ref={ref}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          variants={staggerContainer}
          className="grid grid-cols-1 md:grid-cols-3 gap-8"
        >
          {stats.map((stat, index) => (
            <motion.div 
              key={index} 
              variants={fadeInUp}
              className="text-center p-8 bg-white/5 border border-white/5 backdrop-blur-sm"
              data-testid={`stat-${stat.label.toLowerCase().replace(' ', '-')}`}
            >
              <stat.icon className="w-8 h-8 text-[#D4AF37] mx-auto mb-4" />
              <Counter end={stat.number} suffix={stat.suffix} />
              <p className="font-heading text-sm tracking-widest uppercase text-gray-400 mt-4">
                {stat.label}
              </p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

// About Section
const AboutSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section id="about" className="py-20 md:py-32 bg-[#050505]" data-testid="about-section">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div 
          ref={ref}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          variants={staggerContainer}
          className="grid md:grid-cols-2 gap-12 md:gap-20 items-center"
        >
          {/* Image */}
          <motion.div variants={fadeInUp} className="relative">
            <div className="relative overflow-hidden">
              <img 
                src="https://customer-assets.emergentagent.com/job_saved-redesign/artifacts/jrd32hqv_Praisim%20Solo%20Photo.jpg" 
                alt="Praisim Peter" 
                className="w-full h-auto"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
            </div>
            {/* Decorative element */}
            <div className="absolute -bottom-4 -right-4 w-24 h-24 border border-[#D4AF37]/30" />
          </motion.div>

          {/* Content */}
          <motion.div variants={fadeInUp}>
            <p className="text-[#D4AF37] font-heading text-sm tracking-[0.3em] uppercase mb-4">
              Meet The Visionary & CEO
            </p>
            <h2 className="font-heading text-4xl md:text-5xl font-bold uppercase tracking-tight text-white mb-6">
              Praisim Peter
            </h2>
            <div className="section-divider mb-8" />
            <p className="text-gray-400 text-lg leading-relaxed mb-6">
              Praisim Peter is an apostle and revivalist who has seen hundreds of thousands come to Jesus through crusades and revival meetings across the United States and India. He oversees 154 churches and hosts large-scale gatherings across India and now America.
            </p>
            <p className="text-gray-400 text-lg leading-relaxed mb-8">
              His ministry carries a strong anointing for salvation, healing, and Holy Spirit encounters. With a passion to see the lost found and the broken restored, Praisim Peter brings the undiluted Gospel with power and demonstration of the Spirit.
            </p>
            <blockquote className="border-l-2 border-[#D4AF37] pl-6 font-accent text-xl text-white italic">
              "The God of Abraham, Isaac, and Jacob — My God who moves in every revival meeting — will move in your city!"
              <span className="block text-[#D4AF37] text-base mt-2 not-italic">— Praisim Peter</span>
            </blockquote>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

// What to Expect Section
const ExpectSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const features = [
    {
      icon: Mic,
      title: "Powerful Gospel Preaching",
      description: "Hear the uncompromising Word of God that transforms lives"
    },
    {
      icon: Sparkles,
      title: "Miracles & Healing",
      description: "Witness the supernatural power of Jesus Christ at work"
    },
    {
      icon: Flame,
      title: "Deliverance",
      description: "Break free from bondage, addiction, and oppression"
    },
    {
      icon: HandHeart,
      title: "Freedom & Restoration",
      description: "Experience complete restoration in body, mind, and spirit"
    },
    {
      icon: Cross,
      title: "Salvations",
      description: "Give your life to Jesus and experience true transformation"
    },
    {
      icon: BookOpen,
      title: "Holy Spirit Encounter",
      description: "Receive a fresh baptism of the Holy Spirit and His power"
    }
  ];

  return (
    <section className="py-20 md:py-32 bg-[#0A0A0B]" data-testid="expect-section">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div 
          ref={ref}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          variants={staggerContainer}
        >
          <motion.div variants={fadeInUp} className="text-center mb-16">
            <p className="text-[#D4AF37] font-heading text-sm tracking-[0.3em] uppercase mb-4">
              Come Expecting
            </p>
            <h2 className="font-heading text-4xl md:text-6xl font-bold uppercase tracking-tight text-white">
              What to Expect
            </h2>
          </motion.div>

          <motion.div 
            variants={staggerContainer}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            {features.map((feature, index) => (
              <motion.div 
                key={index}
                variants={fadeInUp}
                className="card-hover p-8 bg-[#050505]"
                data-testid={`feature-${index}`}
              >
                <div className="icon-container">
                  <feature.icon className="w-7 h-7 text-[#D4AF37]" />
                </div>
                <h3 className="font-heading text-xl uppercase tracking-wide text-white mb-3">
                  {feature.title}
                </h3>
                <p className="text-gray-400">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

// Featured Event Section
const EventSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section 
      id="events" 
      className="py-20 md:py-32 relative"
      style={{ 
        backgroundImage: `url('https://customer-assets.emergentagent.com/job_saved-redesign/artifacts/targi8if_Ranchi.jpg')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }}
      data-testid="event-section"
    >
      <div className="absolute inset-0 bg-black/85" />
      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <motion.div 
          ref={ref}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          variants={staggerContainer}
        >
          <motion.div variants={fadeInUp} className="text-center mb-12">
            <p className="text-[#D4AF37] font-heading text-sm tracking-[0.3em] uppercase mb-4">
              Don't Miss This
            </p>
            <h2 className="font-heading text-4xl md:text-6xl font-bold uppercase tracking-tight text-white">
              Upcoming Revival
            </h2>
          </motion.div>

          <motion.div 
            variants={fadeInUp}
            className="max-w-3xl mx-auto bg-[#0A0A0B]/90 backdrop-blur-lg border border-[#D4AF37]/30 p-8 md:p-12"
          >
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-8">
              <div>
                <p className="text-[#D4AF37] font-accent text-lg mb-2">Festival of Blessings</p>
                <h3 className="font-heading text-3xl md:text-4xl uppercase tracking-tight text-white mb-4">
                  Santa Rosa Revival
                </h3>
                <div className="flex flex-col gap-3 text-gray-300">
                  <div className="flex items-center gap-3">
                    <Calendar className="w-5 h-5 text-[#D4AF37]" />
                    <span>March 6 & 7, 2026</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Clock className="w-5 h-5 text-[#D4AF37]" />
                    <span>6:00 PM</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <MapPin className="w-5 h-5 text-[#D4AF37]" />
                    <span>Quintanilla Convention Center, Santa Rosa, TX</span>
                  </div>
                </div>
              </div>
              <div className="flex flex-col gap-4">
                <a 
                  href="https://santarosarevival.com/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="btn-primary inline-flex items-center justify-center gap-2 pulse-gold"
                  data-testid="event-register-btn"
                >
                  Register Free <ExternalLink size={16} />
                </a>
                <p className="text-center text-gray-500 text-sm">Limited Seats Available</p>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

// Video Section
const VideoSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section id="watch" className="py-20 md:py-32 bg-[#050505]" data-testid="video-section">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div 
          ref={ref}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          variants={staggerContainer}
        >
          <motion.div variants={fadeInUp} className="text-center mb-12">
            <p className="text-[#D4AF37] font-heading text-sm tracking-[0.3em] uppercase mb-4">
              Witness The Move Of God
            </p>
            <h2 className="font-heading text-4xl md:text-6xl font-bold uppercase tracking-tight text-white mb-4">
              Watch The Testimonies
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              See the powerful testimonies and miracles from our recent crusades. What God has done for others, He will do for you!
            </p>
          </motion.div>

          <motion.div variants={fadeInUp} className="grid md:grid-cols-2 gap-8">
            {/* Main Video */}
            <div className="video-container border border-white/10">
              <iframe 
                src="https://www.youtube.com/embed/OcEXVACwvZg" 
                title="Move of God in India - 200,000+ received Jesus"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                data-testid="main-video"
              />
            </div>
            
            {/* Secondary Video */}
            <div className="video-container border border-white/10">
              <iframe 
                src="https://www.youtube.com/embed/dW59LFPCh9k" 
                title="Ministry Testimony"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                data-testid="secondary-video"
              />
            </div>
          </motion.div>

          <motion.div variants={fadeInUp} className="text-center mt-8">
            <a 
              href="https://youtu.be/OcEXVACwvZg?si=CyfHQUfMufgvem0k" 
              target="_blank" 
              rel="noopener noreferrer"
              className="btn-secondary inline-flex items-center gap-2"
              data-testid="youtube-channel-btn"
            >
              <Youtube size={20} /> Watch More on YouTube
            </a>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

// Invite to Speak Section
const InviteSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const [formData, setFormData] = useState({
    church_name: "",
    pastor_name: "",
    email: "",
    phone: "",
    city: "",
    state: "",
    event_type: "Revival Meeting",
    proposed_date: "",
    expected_attendance: "",
    message: ""
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/speaking-invitations`, formData);
      setSubmitted(true);
      setFormData({
        church_name: "", pastor_name: "", email: "", phone: "", city: "", state: "",
        event_type: "Revival Meeting", proposed_date: "", expected_attendance: "", message: ""
      });
    } catch (error) {
      console.error("Error submitting invitation:", error);
    }
    setSubmitting(false);
  };

  return (
    <section 
      id="invite" 
      className="py-20 md:py-32 bg-[#0A0A0B]"
      data-testid="invite-section"
    >
      <div className="max-w-7xl mx-auto px-6">
        <motion.div 
          ref={ref}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          variants={staggerContainer}
        >
          <motion.div variants={fadeInUp} className="text-center mb-12">
            <p className="text-[#D4AF37] font-heading text-sm tracking-[0.3em] uppercase mb-4">
              Bring Revival To Your City
            </p>
            <h2 className="font-heading text-4xl md:text-6xl font-bold uppercase tracking-tight text-white">
              Invite Praisim Peter
            </h2>
            <p className="text-gray-400 mt-4 max-w-2xl mx-auto">
              Want to host a revival meeting, crusade, or conference at your church? Fill out the form below and our team will get back to you.
            </p>
          </motion.div>

          <motion.div variants={fadeInUp} className="max-w-3xl mx-auto">
            {submitted ? (
              <div className="text-center p-12 bg-[#050505] border border-[#D4AF37]/30">
                <div className="w-16 h-16 bg-[#D4AF37]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Church className="w-8 h-8 text-[#D4AF37]" />
                </div>
                <h3 className="font-heading text-2xl uppercase text-white mb-4">Invitation Received!</h3>
                <p className="text-gray-400">Thank you for your invitation. Our team will review your request and contact you soon.</p>
                <button 
                  onClick={() => setSubmitted(false)} 
                  className="btn-secondary mt-6"
                  data-testid="submit-another-invite-btn"
                >
                  Submit Another Invitation
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6 bg-[#050505] p-8 border border-white/5" data-testid="invite-form">
                <div className="grid md:grid-cols-2 gap-6">
                  <input
                    type="text"
                    placeholder="Church Name *"
                    value={formData.church_name}
                    onChange={(e) => setFormData({...formData, church_name: e.target.value})}
                    className="input-minimal"
                    required
                    data-testid="invite-church-name"
                  />
                  <input
                    type="text"
                    placeholder="Pastor/Leader Name *"
                    value={formData.pastor_name}
                    onChange={(e) => setFormData({...formData, pastor_name: e.target.value})}
                    className="input-minimal"
                    required
                    data-testid="invite-pastor-name"
                  />
                </div>
                <div className="grid md:grid-cols-2 gap-6">
                  <input
                    type="email"
                    placeholder="Email *"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="input-minimal"
                    required
                    data-testid="invite-email"
                  />
                  <input
                    type="tel"
                    placeholder="Phone *"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    className="input-minimal"
                    required
                    data-testid="invite-phone"
                  />
                </div>
                <div className="grid md:grid-cols-2 gap-6">
                  <input
                    type="text"
                    placeholder="City *"
                    value={formData.city}
                    onChange={(e) => setFormData({...formData, city: e.target.value})}
                    className="input-minimal"
                    required
                    data-testid="invite-city"
                  />
                  <input
                    type="text"
                    placeholder="State *"
                    value={formData.state}
                    onChange={(e) => setFormData({...formData, state: e.target.value})}
                    className="input-minimal"
                    required
                    data-testid="invite-state"
                  />
                </div>
                <div className="grid md:grid-cols-3 gap-6">
                  <select
                    value={formData.event_type}
                    onChange={(e) => setFormData({...formData, event_type: e.target.value})}
                    className="input-minimal bg-transparent"
                    data-testid="invite-event-type"
                  >
                    <option value="Revival Meeting">Revival Meeting</option>
                    <option value="Crusade">Crusade</option>
                    <option value="Conference">Conference</option>
                    <option value="Sunday Service">Sunday Service</option>
                    <option value="Special Event">Special Event</option>
                  </select>
                  <input
                    type="text"
                    placeholder="Proposed Date"
                    value={formData.proposed_date}
                    onChange={(e) => setFormData({...formData, proposed_date: e.target.value})}
                    className="input-minimal"
                    data-testid="invite-date"
                  />
                  <input
                    type="text"
                    placeholder="Expected Attendance"
                    value={formData.expected_attendance}
                    onChange={(e) => setFormData({...formData, expected_attendance: e.target.value})}
                    className="input-minimal"
                    data-testid="invite-attendance"
                  />
                </div>
                <textarea
                  placeholder="Tell us about your church and why you'd like to host a revival..."
                  value={formData.message}
                  onChange={(e) => setFormData({...formData, message: e.target.value})}
                  className="input-minimal min-h-[120px] resize-none"
                  data-testid="invite-message"
                />
                <button 
                  type="submit" 
                  className="btn-primary w-full flex items-center justify-center gap-2"
                  disabled={submitting}
                  data-testid="invite-submit-btn"
                >
                  {submitting ? "Submitting..." : "Submit Invitation"} <Send size={18} />
                </button>
              </form>
            )}
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

// Prayer Request Section
const PrayerSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    prayer_request: ""
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/prayer-requests`, formData);
      setSubmitted(true);
      setFormData({ first_name: "", last_name: "", email: "", phone: "", prayer_request: "" });
    } catch (error) {
      console.error("Error submitting prayer request:", error);
    }
    setSubmitting(false);
  };

  return (
    <section 
      id="prayer" 
      className="py-20 md:py-32 relative prayer-section"
      style={{ 
        backgroundImage: `url('https://customer-assets.emergentagent.com/job_saved-redesign/artifacts/ylfwlxie_9f496856-2a65-40f4-b9ad-737c724a8022.jpg')`
      }}
      data-testid="prayer-section"
    >
      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <motion.div 
          ref={ref}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          variants={staggerContainer}
        >
          <motion.div variants={fadeInUp} className="text-center mb-12">
            <p className="text-[#D4AF37] font-heading text-sm tracking-[0.3em] uppercase mb-4">
              We're Praying For You
            </p>
            <h2 className="font-heading text-4xl md:text-6xl font-bold uppercase tracking-tight text-white">
              Submit Your Prayer Request
            </h2>
          </motion.div>

          <motion.div variants={fadeInUp} className="max-w-2xl mx-auto">
            {submitted ? (
              <div className="text-center p-12 bg-[#0A0A0B]/80 border border-[#D4AF37]/30">
                <div className="w-16 h-16 bg-[#D4AF37]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Heart className="w-8 h-8 text-[#D4AF37]" />
                </div>
                <h3 className="font-heading text-2xl uppercase text-white mb-4">Thank You!</h3>
                <p className="text-gray-400">Your prayer request has been received. We are lifting you up in prayer.</p>
                <button 
                  onClick={() => setSubmitted(false)} 
                  className="btn-secondary mt-6"
                  data-testid="submit-another-btn"
                >
                  Submit Another Request
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6" data-testid="prayer-form">
                <div className="grid md:grid-cols-2 gap-6">
                  <input
                    type="text"
                    placeholder="First Name"
                    value={formData.first_name}
                    onChange={(e) => setFormData({...formData, first_name: e.target.value})}
                    className="input-minimal"
                    required
                    data-testid="prayer-first-name"
                  />
                  <input
                    type="text"
                    placeholder="Last Name"
                    value={formData.last_name}
                    onChange={(e) => setFormData({...formData, last_name: e.target.value})}
                    className="input-minimal"
                    required
                    data-testid="prayer-last-name"
                  />
                </div>
                <div className="grid md:grid-cols-2 gap-6">
                  <input
                    type="email"
                    placeholder="Email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="input-minimal"
                    required
                    data-testid="prayer-email"
                  />
                  <input
                    type="tel"
                    placeholder="Phone"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    className="input-minimal"
                    required
                    data-testid="prayer-phone"
                  />
                </div>
                <textarea
                  placeholder="Share your prayer request..."
                  value={formData.prayer_request}
                  onChange={(e) => setFormData({...formData, prayer_request: e.target.value})}
                  className="input-minimal min-h-[150px] resize-none"
                  required
                  data-testid="prayer-request-text"
                />
                <button 
                  type="submit" 
                  className="btn-primary w-full flex items-center justify-center gap-2"
                  disabled={submitting}
                  data-testid="prayer-submit-btn"
                >
                  {submitting ? "Submitting..." : "Submit Prayer Request"} <Send size={18} />
                </button>
              </form>
            )}
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

// Lead Gen / Stay Connected Section
const LeadGenSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const [formData, setFormData] = useState({ name: "", email: "", phone: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads`, { ...formData, source: "website" });
      setSubmitted(true);
      setFormData({ name: "", email: "", phone: "" });
    } catch (error) {
      console.error("Error submitting lead:", error);
    }
    setSubmitting(false);
  };

  return (
    <section className="py-16 bg-[#050505] border-t border-b border-white/5" data-testid="leadgen-section">
      <div className="max-w-4xl mx-auto px-6">
        <motion.div 
          ref={ref}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          variants={staggerContainer}
          className="text-center"
        >
          <motion.div variants={fadeInUp}>
            <h3 className="font-heading text-2xl md:text-3xl uppercase tracking-tight text-white mb-2">
              Stay Connected
            </h3>
            <p className="text-gray-400 mb-8">
              Get updates on upcoming events, revivals, and ministry news
            </p>
          </motion.div>

          {submitted ? (
            <motion.div variants={fadeInUp} className="text-[#D4AF37] font-heading">
              Thank you! You're now connected with us.
            </motion.div>
          ) : (
            <motion.form 
              variants={fadeInUp}
              onSubmit={handleSubmit} 
              className="flex flex-col md:flex-row gap-4 justify-center"
              data-testid="leadgen-form"
            >
              <input
                type="text"
                placeholder="Your Name"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                className="input-minimal md:w-48 text-center md:text-left"
                required
                data-testid="leadgen-name"
              />
              <input
                type="email"
                placeholder="Email Address"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                className="input-minimal md:w-56 text-center md:text-left"
                required
                data-testid="leadgen-email"
              />
              <input
                type="tel"
                placeholder="Phone Number"
                value={formData.phone}
                onChange={(e) => setFormData({...formData, phone: e.target.value})}
                className="input-minimal md:w-48 text-center md:text-left"
                required
                data-testid="leadgen-phone"
              />
              <button 
                type="submit" 
                className="btn-primary"
                disabled={submitting}
                data-testid="leadgen-submit-btn"
              >
                {submitting ? "..." : "Connect"}
              </button>
            </motion.form>
          )}
        </motion.div>
      </div>
    </section>
  );
};

// Give Section
const GiveSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section id="give" className="py-20 md:py-32 bg-[#0A0A0B]" data-testid="give-section">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div 
          ref={ref}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          variants={staggerContainer}
          className="grid md:grid-cols-2 gap-12 items-center"
        >
          {/* Image Side */}
          <motion.div variants={fadeInUp} className="relative order-2 md:order-1">
            <img 
              src="https://customer-assets.emergentagent.com/job_saved-redesign/artifacts/c7r2wpes_India-Report-Crowd-1920x1187-Benny-Hinn-Ministries.jpg" 
              alt="Revival Crowd Worshipping" 
              className="w-full h-auto"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/50 to-transparent" />
          </motion.div>

          {/* Content Side */}
          <motion.div variants={fadeInUp} className="order-1 md:order-2">
            <p className="text-[#D4AF37] font-heading text-sm tracking-[0.3em] uppercase mb-4">
              Partner With Us
            </p>
            <h2 className="font-heading text-4xl md:text-5xl font-bold uppercase tracking-tight text-white mb-6">
              Give, and It Shall Be Given Unto You
            </h2>
            <div className="section-divider mb-8" />
            <p className="text-gray-400 text-lg leading-relaxed mb-6">
              Every soul reached costs less than $1. Your partnership helps us bring revival meetings to cities across America and around the world, reaching the lost with the Gospel of Jesus Christ.
            </p>
            
            <div className="grid grid-cols-3 gap-4 mb-8">
              <div className="text-center p-4 bg-white/5 border border-white/5">
                <span className="gold-text font-heading text-2xl font-bold">200K+</span>
                <p className="text-gray-500 text-sm mt-1">Souls Reached</p>
              </div>
              <div className="text-center p-4 bg-white/5 border border-white/5">
                <span className="gold-text font-heading text-2xl font-bold">154+</span>
                <p className="text-gray-500 text-sm mt-1">Churches</p>
              </div>
              <div className="text-center p-4 bg-white/5 border border-white/5">
                <span className="gold-text font-heading text-2xl font-bold">&lt;$1</span>
                <p className="text-gray-500 text-sm mt-1">Per Soul</p>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <a 
                href="https://donorbox.org/indiasavedmission" 
                target="_blank" 
                rel="noopener noreferrer"
                className="btn-primary inline-flex items-center justify-center gap-2"
                data-testid="give-now-btn"
              >
                Give Now <ExternalLink size={16} />
              </a>
              <a 
                href="https://donorbox.org/indiasavedmission" 
                target="_blank" 
                rel="noopener noreferrer"
                className="btn-secondary inline-flex items-center justify-center gap-2"
                data-testid="monthly-partner-btn"
              >
                Become Monthly Partner
              </a>
            </div>
            <p className="text-gray-600 text-sm mt-4">
              Your donation is tax-deductible and goes directly to reaching souls for Christ
            </p>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  return (
    <footer className="py-16 bg-[#050505] border-t border-white/5" data-testid="footer">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid md:grid-cols-3 gap-12 mb-12">
          {/* Brand */}
          <div>
            <h3 className="font-heading text-2xl tracking-wider text-white mb-4">
              AMERICA <span className="gold-text">SAVED</span>
            </h3>
            <p className="text-gray-500 mb-6">
              America Saved Mission & India Saved Mission — Born from One Vision. Assigned to Two Nations.
            </p>
            <div className="flex gap-4">
              <a 
                href="https://www.instagram.com/praisim.peter" 
                target="_blank" 
                rel="noopener noreferrer"
                className="social-icon"
                data-testid="social-instagram"
              >
                <Instagram size={20} />
              </a>
              <a 
                href="https://www.facebook.com/praisimpeter" 
                target="_blank" 
                rel="noopener noreferrer"
                className="social-icon"
                data-testid="social-facebook"
              >
                <Facebook size={20} />
              </a>
              <a 
                href="https://youtu.be/OcEXVACwvZg?si=CyfHQUfMufgvem0k" 
                target="_blank" 
                rel="noopener noreferrer"
                className="social-icon"
                data-testid="social-youtube"
              >
                <Youtube size={20} />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-heading text-sm tracking-widest uppercase text-white mb-6">Quick Links</h4>
            <ul className="space-y-3">
              <li><a href="#about" className="text-gray-500 hover:text-white transition-colors">About</a></li>
              <li><a href="#events" className="text-gray-500 hover:text-white transition-colors">Events</a></li>
              <li><a href="#watch" className="text-gray-500 hover:text-white transition-colors">Watch</a></li>
              <li><a href="#invite" className="text-gray-500 hover:text-white transition-colors">Invite to Speak</a></li>
              <li><a href="#prayer" className="text-gray-500 hover:text-white transition-colors">Prayer Request</a></li>
              <li><a href="https://donorbox.org/indiasavedmission" target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-white transition-colors">Give</a></li>
            </ul>
          </div>

          {/* Connect */}
          <div>
            <h4 className="font-heading text-sm tracking-widest uppercase text-white mb-6">Connect</h4>
            <p className="text-gray-500 mb-4">
              For ministry inquiries and partnership opportunities, reach out through our social media channels.
            </p>
            <a 
              href="https://santarosarevival.com/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-[#D4AF37] hover:text-[#F3E5AB] transition-colors"
            >
              Upcoming Event <ArrowRight size={16} />
            </a>
          </div>
        </div>

        {/* Bottom */}
        <div className="pt-8 border-t border-white/5">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4 mb-4">
            <p className="text-gray-600 text-sm text-center md:text-left">
              © {new Date().getFullYear()} America Saved Mission | India Saved Mission. A Ministry of Praisim Peter.
            </p>
            <p className="text-gray-600 text-sm">
              All rights reserved.
            </p>
          </div>
          <div className="text-center">
            <p className="text-gray-500 text-sm mb-2">
              America Saved Mission & India Saved Mission are the ministry wings of Throne Room Ministries. A 501c3 non profit.
            </p>
            <a 
              href="https://churchmultiplied.com" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-[#D4AF37] text-sm hover:text-[#F3E5AB] transition-colors"
              data-testid="churchmultiplied-link"
            >
              Made with ChurchMultiplied.com
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

// Admin Dashboard Page
const AdminDashboard = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loginData, setLoginData] = useState({ username: "", password: "" });
  const [loginError, setLoginError] = useState("");
  const [activeTab, setActiveTab] = useState("prayer");
  const [data, setData] = useState({
    prayerRequests: [],
    invitations: [],
    leads: [],
    stats: {}
  });
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError("");
    try {
      const response = await axios.post(`${API}/admin/login`, loginData);
      if (response.data.success) {
        setIsAuthenticated(true);
        fetchData();
      }
    } catch (error) {
      setLoginError("Invalid username or password");
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [prayers, invitations, leads, stats] = await Promise.all([
        axios.get(`${API}/prayer-requests`),
        axios.get(`${API}/speaking-invitations`),
        axios.get(`${API}/leads`),
        axios.get(`${API}/admin/stats`)
      ]);
      setData({
        prayerRequests: prayers.data,
        invitations: invitations.data,
        leads: leads.data,
        stats: stats.data
      });
    } catch (error) {
      console.error("Error fetching data:", error);
    }
    setLoading(false);
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setLoginData({ username: "", password: "" });
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center px-6">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <h1 className="font-heading text-3xl tracking-wider text-white mb-2">
              AMERICA <span className="gold-text">SAVED</span>
            </h1>
            <p className="text-gray-500">Admin Dashboard</p>
          </div>
          <form onSubmit={handleLogin} className="bg-[#0A0A0B] p-8 border border-white/10" data-testid="admin-login-form">
            <div className="flex items-center justify-center w-16 h-16 bg-[#D4AF37]/10 rounded-full mx-auto mb-6">
              <Lock className="w-8 h-8 text-[#D4AF37]" />
            </div>
            {loginError && (
              <p className="text-red-500 text-center mb-4">{loginError}</p>
            )}
            <input
              type="text"
              placeholder="Username"
              value={loginData.username}
              onChange={(e) => setLoginData({...loginData, username: e.target.value})}
              className="input-minimal mb-4"
              required
              data-testid="admin-username"
            />
            <input
              type="password"
              placeholder="Password"
              value={loginData.password}
              onChange={(e) => setLoginData({...loginData, password: e.target.value})}
              className="input-minimal mb-6"
              required
              data-testid="admin-password"
            />
            <button type="submit" className="btn-primary w-full" data-testid="admin-login-btn">
              Login
            </button>
          </form>
          <p className="text-center mt-6">
            <a href="/" className="text-gray-500 hover:text-white transition-colors">← Back to Website</a>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Admin Header */}
      <header className="bg-[#0A0A0B] border-b border-white/10 px-6 py-4">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <h1 className="font-heading text-xl tracking-wider text-white">
            AMERICA <span className="gold-text">SAVED</span> <span className="text-gray-500 text-sm ml-2">Admin</span>
          </h1>
          <button 
            onClick={handleLogout}
            className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
            data-testid="admin-logout-btn"
          >
            <LogOut size={18} /> Logout
          </button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-[#0A0A0B] p-6 border border-white/10">
            <p className="text-gray-500 text-sm mb-1">Prayer Requests</p>
            <p className="font-heading text-3xl gold-text">{data.stats.prayer_requests || 0}</p>
          </div>
          <div className="bg-[#0A0A0B] p-6 border border-white/10">
            <p className="text-gray-500 text-sm mb-1">Speaking Invitations</p>
            <p className="font-heading text-3xl gold-text">{data.stats.speaking_invitations || 0}</p>
          </div>
          <div className="bg-[#0A0A0B] p-6 border border-white/10">
            <p className="text-gray-500 text-sm mb-1">Leads</p>
            <p className="font-heading text-3xl gold-text">{data.stats.leads || 0}</p>
          </div>
          <div className="bg-[#0A0A0B] p-6 border border-white/10">
            <p className="text-gray-500 text-sm mb-1">Contact Messages</p>
            <p className="font-heading text-3xl gold-text">{data.stats.contact_messages || 0}</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-4 mb-6 border-b border-white/10 pb-4">
          {[
            { id: "prayer", label: "Prayer Requests" },
            { id: "invitations", label: "Speaking Invitations" },
            { id: "leads", label: "Leads" }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`font-heading text-sm tracking-widest uppercase transition-colors ${
                activeTab === tab.id ? 'text-[#D4AF37]' : 'text-gray-500 hover:text-white'
              }`}
              data-testid={`admin-tab-${tab.id}`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        {loading ? (
          <p className="text-gray-500">Loading...</p>
        ) : (
          <div className="space-y-4">
            {activeTab === "prayer" && data.prayerRequests.map((item, index) => (
              <div key={index} className="bg-[#0A0A0B] p-6 border border-white/10">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="font-heading text-lg text-white">{item.first_name} {item.last_name}</h3>
                    <p className="text-gray-500 text-sm">{item.email} | {item.phone}</p>
                  </div>
                  <span className="text-gray-600 text-xs">{new Date(item.created_at).toLocaleDateString()}</span>
                </div>
                <p className="text-gray-300">{item.prayer_request}</p>
              </div>
            ))}

            {activeTab === "invitations" && data.invitations.map((item, index) => (
              <div key={index} className="bg-[#0A0A0B] p-6 border border-white/10">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="font-heading text-lg text-white">{item.church_name}</h3>
                    <p className="text-[#D4AF37]">{item.pastor_name}</p>
                    <p className="text-gray-500 text-sm">{item.city}, {item.state}</p>
                  </div>
                  <span className="text-gray-600 text-xs">{new Date(item.created_at).toLocaleDateString()}</span>
                </div>
                <div className="grid md:grid-cols-3 gap-4 text-sm mb-4">
                  <div><span className="text-gray-500">Event Type:</span> <span className="text-white">{item.event_type}</span></div>
                  <div><span className="text-gray-500">Proposed Date:</span> <span className="text-white">{item.proposed_date}</span></div>
                  <div><span className="text-gray-500">Expected:</span> <span className="text-white">{item.expected_attendance}</span></div>
                </div>
                <p className="text-gray-500 text-sm mb-2">{item.email} | {item.phone}</p>
                <p className="text-gray-300">{item.message}</p>
              </div>
            ))}

            {activeTab === "leads" && data.leads.map((item, index) => (
              <div key={index} className="bg-[#0A0A0B] p-4 border border-white/10 flex justify-between items-center">
                <div>
                  <h3 className="font-heading text-white">{item.name}</h3>
                  <p className="text-gray-500 text-sm">{item.email} | {item.phone}</p>
                </div>
                <span className="text-gray-600 text-xs">{new Date(item.created_at).toLocaleDateString()}</span>
              </div>
            ))}

            {((activeTab === "prayer" && data.prayerRequests.length === 0) ||
              (activeTab === "invitations" && data.invitations.length === 0) ||
              (activeTab === "leads" && data.leads.length === 0)) && (
              <p className="text-gray-500 text-center py-8">No data yet</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// Main Page Component
const MainPage = () => {
  return (
    <>
      <Navigation />
      <HeroSection />
      <StatsSection />
      <AboutSection />
      <ExpectSection />
      <EventSection />
      <VideoSection />
      <InviteSection />
      <PrayerSection />
      <LeadGenSection />
      <GiveSection />
      <Footer />
    </>
  );
};

// Main App Component
function App() {
  return (
    <div className="App bg-[#050505] min-h-screen">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<MainPage />} />
          <Route path="/admin" element={<AdminDashboard />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
